import React, { useState } from "react";

import { isEmpty } from "lodash/fp";

import {
  FormControlLabel,
  FormControlLabelProps,
  Typography,
} from "@mui/material";

import IconHeading from "@app/analytics/components/IconHeading";
import {
  EmptyNote,
  NoteSectionHeader,
  NoteSubSection as NoteSubSectionContainer,
} from "@app/analytics/components/reports/styles";
import EditedChip from "@app/analytics/MeetingReport/components/EditedChip";
import useTranscript from "@app/analytics/MeetingReport/hooks/useTranscript";
import useEditStore from "@app/analytics/MeetingReport/stores/edits";
import useSearchStore from "@app/analytics/MeetingReport/stores/search";
import type { NoteItemOrChapter } from "@app/analytics/MeetingReport/types";
import {
  getChapterDescriptionSearchResultKey,
  getChaptersAndTopicsCopyContent,
} from "@app/analytics/MeetingReport/utils";
import useMeasure from "@app/common/hooks/useMeasure";
import useResponsiveView from "@app/common/hooks/useResponsiveView";
import type { TranscriptSummary } from "@app/common/types";

import { Row } from "@rui/Stack";
import Switch from "@rui/Switch";

import TopicsWithChapters from "./TopicsWithChapters";
import CopyAndEditActionsRow from "../CopyAndEditActionsRow";
import NoteCard from "../NoteCard";
import NoteSubSection from "../NoteSubSection";
import { NoteSectionHeaderTitleAndActionsContainer } from "../styles";

const BREAKPOINTS = {
  descriptionLabel: 465,
  editedDescriptionLabel: 525,
};

const ChapterDescriptionsToggle = ({
  checked,
  disabled,
  labelPlacement,
  onChange,
  showLabel,
}: {
  checked: boolean;
  disabled: boolean;
  labelPlacement?: FormControlLabelProps["labelPlacement"];
  onChange: (checked: boolean) => void;
  showLabel: boolean;
}) => {
  const handleToggle = (_e: React.ChangeEvent, checked: boolean) =>
    onChange(checked);

  return (
    <FormControlLabel
      labelPlacement={labelPlacement}
      control={
        <Switch
          disabled={disabled}
          checked={checked}
          onChange={handleToggle}
          sx={{ ml: showLabel ? 1 : 0 }}
        />
      }
      label={
        <Typography
          variant="bodySecondaryMed"
          color="shades.neutral.600"
          sx={{ display: showLabel ? "block" : "none" }}
        >
          Descriptions
        </Typography>
      }
      sx={{ ml: 0 }}
    />
  );
};

const ChaptersAndTopics = ({
  canEdit,
  disableEditing,
  forceExpand,
  isEdited,
  isNotetakerReport,
  onItemClick,
  shouldUpsell,
  tooltip,
  topics,
}: {
  canEdit: boolean;
  disableEditing: boolean;
  forceExpand: boolean;
  isEdited: boolean;
  isNotetakerReport: boolean;
  onItemClick: (item: NoteItemOrChapter) => (() => void) | undefined;
  shouldUpsell: boolean;
  tooltip?: string;
  topics: TranscriptSummary["topics"];
}) => {
  const { inDesktopView } = useResponsiveView();
  const chapterSearchResults = useSearchStore(
    (state) => state.searchResultsByNoteTypeAndId.chapter
  );

  const [showChapterDescriptions, setShowChapterDescriptions] = useState(false);
  const {
    summary: { chapters },
  } = useTranscript();

  const [containerRef, , { innerWidth }] = useMeasure();

  const isEditingNotes = useEditStore(
    (state) => state.editingSection === "notes"
  );

  const hasChapters = !isEmpty(chapters);
  const hasTopics = !isEmpty(topics) || isNotetakerReport || isEditingNotes;

  const chapterDescriptionsHaveSearchResults = chapters.some(
    (c) =>
      !isEmpty(
        chapterSearchResults.get(getChapterDescriptionSearchResultKey(c))
      )
  );

  const chapterDescriptionsVisible =
    forceExpand ||
    showChapterDescriptions ||
    chapterDescriptionsHaveSearchResults ||
    isEditingNotes;

  const showChapterDescriptionToggle =
    hasChapters && !shouldUpsell && !isEditingNotes;

  const descriptionBreakpoint = isEdited
    ? BREAKPOINTS.editedDescriptionLabel
    : BREAKPOINTS.descriptionLabel;

  return (
    <NoteCard
      forceExpand={forceExpand}
      isEditing={isEditingNotes}
      header={
        <NoteSectionHeader ref={containerRef}>
          <Row variant="apart" width="100%">
            <NoteSectionHeaderTitleAndActionsContainer>
              <IconHeading type="topics">
                {hasTopics && !hasChapters ? "Topics" : "Chapters & Topics"}
              </IconHeading>
              <CopyAndEditActionsRow
                isEditing={isEditingNotes}
                canEdit={canEdit}
                canCopy={Boolean(chapters.length) || Boolean(topics.length)}
                copyValue={getChaptersAndTopicsCopyContent({
                  chapters,
                  topics,
                })}
                name="chapters-and-topics"
              />
            </NoteSectionHeaderTitleAndActionsContainer>
            {inDesktopView && (
              <Row variant="right" spacing={1}>
                {isEdited && <EditedChip />}
                {showChapterDescriptionToggle && (
                  <ChapterDescriptionsToggle
                    checked={chapterDescriptionsVisible}
                    disabled={chapterDescriptionsHaveSearchResults}
                    labelPlacement="start"
                    onChange={setShowChapterDescriptions}
                    showLabel={innerWidth > descriptionBreakpoint}
                  />
                )}
              </Row>
            )}
          </Row>
        </NoteSectionHeader>
      }
      wrapperProps={{
        disabled: disableEditing,
        disabledTooltip: tooltip,
      }}
    >
      {hasChapters ? (
        <>
          {showChapterDescriptionToggle && !inDesktopView && (
            <ChapterDescriptionsToggle
              checked={chapterDescriptionsVisible}
              disabled={chapterDescriptionsHaveSearchResults}
              onChange={setShowChapterDescriptions}
              showLabel
            />
          )}
          <TopicsWithChapters
            chapters={chapters}
            forceExpand={!inDesktopView}
            onItemClick={onItemClick}
            shouldUpsell={shouldUpsell}
            showChapterDescriptions={chapterDescriptionsVisible}
          />
        </>
      ) : hasTopics ? (
        <NoteSubSection
          isNotetakerReport={isNotetakerReport}
          items={topics}
          onItemClick={onItemClick}
          shouldUpsell={shouldUpsell}
          type="topic"
          maxItems={inDesktopView ? undefined : topics.length}
        />
      ) : (
        <NoteSubSectionContainer>
          <EmptyNote>No chapters or topics generated</EmptyNote>
        </NoteSubSectionContainer>
      )}
      {isEdited && !inDesktopView && <EditedChip />}
    </NoteCard>
  );
};

export default ChaptersAndTopics;
